import React, { useState, useEffect } from 'react';

import { LogoTwitter, LogoFacebook } from '@eb/eds-iconography';
import { Icon } from '@eb/eds-icon';
import { Tabs } from '@eb/eds-tabs';

interface SocialWidgetProps {
    twitter?: string;
    facebook?: string;
}

type SocialKeys = keyof SocialWidgetProps;

const TwitterWidget = (props: { handle: string }) => (
    <a
        className="twitter-timeline"
        data-height="555"
        href={`https://twitter.com/${props.handle}?ref_src=twsrc%5Etfw`}
    >
        Tweets by {props.handle}
    </a>
);

const FacebookWidget = (props: { facebookUrl: string; orgName: string }) => {
    useEffect(() => {
        if (props.facebookUrl) {
            //load facebook SDK
            const mountPoint = document.getElementsByTagName('body')[0];
            const facebookSDK = document.createElement('script');
            //TODO: pass language/locale into the script URL here
            facebookSDK.src =
                'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v8.0';
            facebookSDK.crossOrigin = 'anonymous';
            facebookSDK.nonce = 'CQo7gE6e';
            facebookSDK.async = true;
            facebookSDK.defer = true;
            mountPoint.appendChild(facebookSDK);
        }
    }, [props.facebookUrl]);

    return (
        <div style={{ minHeight: '570px' }}>
            <div
                style={{ display: 'flex', justifyContent: 'center' }}
                className="fb-page"
                data-href={`https://www.facebook.com/${props.facebookUrl}`}
                data-width="330"
                data-height="555"
                data-small-header="true"
                data-adapt-container-width="true"
                data-hide-cover="false"
                data-show-facepile="true"
                data-show-posts="true"
                data-lazy="true"
            >
                <div className="fb-xfbml-parse-ignore">
                    <blockquote
                        cite={`https://www.facebook.com/${props.facebookUrl}`}
                    >
                        <a
                            href={`https://www.facebook.com/${props.facebookUrl}`}
                        >
                            {props.orgName}
                        </a>
                    </blockquote>
                </div>
            </div>
        </div>
    );
};

const facebookData = {
    SocialContent: (props: { data: string }) => (
        <FacebookWidget facebookUrl={props.data} orgName={props.data} />
    ),
    TabHeader: (props: { color: string }) => (
        <Icon type={<LogoFacebook />} color={props.color} />
    ),
};

const twitterData = {
    SocialContent: (props: { data: string }) => (
        <TwitterWidget handle={props.data} />
    ),
    TabHeader: (props: { color: string }) => (
        <Icon type={<LogoTwitter />} color={props.color} />
    ),
};

const socialMap: {
    [index: string]: { SocialContent: Function; TabHeader: Function };
} = {
    facebook: facebookData,
    twitter: twitterData,
};

export const SocialWidget: React.SFC<SocialWidgetProps> = (props) => {
    const [selectedTab, setTab] = useState(0);
    const includeTwitter = !!props.twitter ? 'twitter' : '';
    const includeFacebook = !!props.facebook ? 'facebook' : '';
    const tabItems = [includeFacebook, includeTwitter].filter((x) => !!x);

    const formattedTabItems = tabItems.map((item, index) => {
        const { SocialContent, TabHeader } = socialMap[item];

        const isActive = selectedTab === index;
        const socialType: SocialKeys = item as SocialKeys;
        const profileInfo = props[socialType];

        const content = <SocialContent data={profileInfo} />;
        const displayName = (
            <TabHeader color={isActive ? 'ui-orange' : 'grey-500'} />
        );

        return {
            value: index,
            content,
            displayName,
        };
    });

    return (
        <div className="social-widget">
            <Tabs
                items={formattedTabItems}
                shouldKeepContentMounted={true}
                onTabChange={(id: number) => setTab(id)}
            />
        </div>
    );
};
